import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TablePagination,
  CircularProgress,
  TableContainer,
  TableCell,
  Alert,
  Snackbar,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { styled } from "@mui/system";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { green, red, orange, grey } from "@mui/material/colors";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Tooltip } from "@mui/material";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import BlockIcon from "@mui/icons-material/Block";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import axios from "axios";
import { API_URL } from "../../../Config/api";
import PendingActionsIcon from "@mui/icons-material/PendingActions";

/* ===================== STYLES ===================== */

const OngoingServiceCard = styled(Card)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "0.5fr 1fr 1fr 1fr 0.8fr 1fr 1fr 1fr 1fr 1fr 1fr 0.4fr",
  alignItems: "center",
  height: "3.2rem",

  backgroundColor: "#fff",
  borderRadius: 8,
  boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
  transition: "0.25s",
  "&:hover": {
    backgroundColor: "#F7F7F7",
    borderLeft: "4px solid #a6b4be",
    borderRight: "4px solid #a6b4be",
    boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
  },
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr 1fr",
    height: "auto",
    rowGap: 8,
  },
}));

const CenteredCardContent = styled(CardContent)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  padding: "6px",
  fontSize: "13px",
});

/* ===================== TABLE CONFIG ===================== */

const TABLE_HEADERS = [
  "Sr.No",
  "Event Code",
  "Start Date",
  "End Date",
  "Session",
  "Revalidation",
  "Prof Cost",
  "Total Cost",

  "Feedback",
  "Payment Taken",
];

/* ===================== COMPONENT ===================== */

const ViewApprovepayoutmodal = ({ row }) => {
  const accessToken = localStorage.getItem("token");
  const [tableData, setTableData] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [summary, setSummary] = useState(null); // top level payout
  const [events, setEvents] = useState([]); // table rows

  const [loading, setLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // success | error | warning | info
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "numeric",
      year: "numeric",
    });
  };
  const paginatedData = useMemo(() => {
    if (!Array.isArray(tableData)) return [];
    return tableData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [tableData, page, rowsPerPage]);

  const fetchEventDetails = async () => {
    if (!row) return;

    try {
      setLoading(true);

      const response = await axios.get(
        `${API_URL}/pay_out/event_details/${row.pay_req_id}/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      const data = response?.data?.[0];

      setSummary(data || null); // 👈 summary card
      setTableData(data?.events || []); // 👈 table rows
    } catch (error) {
      console.error("Error fetching event details", error);
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEventDetails();
  }, [row]);

  const payout = summary || {};

  if (!row) return null;

  if (loading) {
    return (
      <Box sx={{ height: "60vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* ===================== TABLE CARD ===================== */}
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <Card sx={{ p: 2, borderRadius: 2 }}>
            <TableContainer
              sx={{
                width: "100%",
                overflowX: "auto",
                maxHeight: "50vh",
              }}
            >
              <Table
                stickyHeader
                sx={{ minWidth: 1200 }} // 👈 VERY IMPORTANT
              >
                <TableHead>
                  <TableRow>
                    {TABLE_HEADERS.map((h, i) => (
                      <TableCell
                        key={h}
                        align="center"
                        sx={{
                          backgroundColor: "#a6b4be",
                          fontWeight: 700,
                          color: "#000",
                          whiteSpace: "nowrap",
                          borderRight:
                            i === TABLE_HEADERS.length - 1
                              ? "none"
                              : "1px solid #fff",

                          // 👇 ONLY header corners
                          borderTopLeftRadius: i === 0 ? 12 : 0,
                          borderTopRightRadius:
                            i === TABLE_HEADERS.length - 1 ? 12 : 0,
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedData.map((item, index) => {
                    const rowIndex = page * rowsPerPage + index;

                    return (
                      <TableRow
                        key={rowIndex}
                        hover
                        sx={{
                          backgroundColor: "#fff",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.08)", // card feel
                          borderRadius: 2,
                          "& td": {
                            borderBottom: "none",
                          },
                          "&:not(:last-child)": {
                            marginBottom: "12px",
                          },
                        }}
                      >
                        <TableCell align="center">{rowIndex + 1}</TableCell>
                        <TableCell align="center">{item.event_code}</TableCell>
                        <TableCell align="center">
                          {formatDate(item.service_start_date)}
                        </TableCell>
                        <TableCell align="center">
                          {formatDate(item.service_end_date)}
                        </TableCell>
                        <TableCell align="center">
                          {item.session_count}
                        </TableCell>
                        <TableCell align="center">
                          {item.revalidation_session}
                        </TableCell>
                        <TableCell align="center">₹{item.prof_cost}</TableCell>
                        <TableCell align="center">
                          ₹{item.total_prof_cost}
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip
                            title={
                              item.feed_back_status
                                ? "Feedback Received"
                                : "Pending Feedback"
                            }
                          >
                            {item.feed_back_status ? (
                              <CheckCircleIcon sx={{ color: green[600] }} />
                            ) : (
                              <PendingActionsIcon sx={{ color: orange[500] }} />
                            )}
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip
                            title={
                              item.payment_with_status === 1
                                ? "Unpaid"
                                : item.payment_with_status === 2
                                  ? "Payment with Professional"
                                  : item.payment_with_status === 3
                                    ? "Payment to Desk"
                                    : item.payment_with_status === 4
                                      ? "complimentary "
                                      : "Unknown Status"
                            }
                          >
                            {/* Status 1: Unpaid (Red Cancel Icon) */}
                            {item.payment_with_status === 1 ? (
                              <CancelIcon sx={{ color: red[500] }} />
                            ) : /* Status 2: Payment with Professional (Orange Warning Icon) */
                            item.payment_with_status === 2 ? (
                              <WarningAmberIcon sx={{ color: orange[500] }} />
                            ) : /* Status 3: Payment to Desk (Green Check Icon) */
                            item.payment_with_status === 3 ? (
                              <CheckCircleIcon sx={{ color: green[600] }} />
                            ) : (
/* Status 4: Complimentary (Blue DoneAll Icon) */item.payment_with_status === 4 ? ( <DoneAllIcon sx={{ color: "#2196f3" }} /> ) : /* Fallback for any other status */
                              /* Fallback for any other status */
                              <ErrorOutlineIcon sx={{ color: grey[500] }} />
                            )}
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              sx={{
                overflow: "hidden",
                "& .MuiTablePagination-toolbar": { paddingRight: 1 },
              }}
              component="div"
              count={tableData.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(+e.target.value);
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </Card>
        </motion.div>
      </AnimatePresence>

      {/* ===================== SUMMARY ===================== */}
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <Card sx={{ mt: 3, borderRadius: 2, background: "#FAFAFA" }}>
            <Table
              size="small"
              sx={{
                "& th, & td": { textAlign: "center", fontSize: 13 },
                "& th": { fontWeight: 700, backgroundColor: "#F1F8E9" },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Total Professional Cost</TableCell>
                  <TableCell>TDS (10%)</TableCell>
                  <TableCell>Conveyance</TableCell>
                  <TableCell>Approne</TableCell>
                  <TableCell>Police Verification</TableCell>
                  <TableCell>Net Amount</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                <TableRow>
                  <TableCell>₹{payout.total_requeststed_amount}</TableCell>

                  <TableCell>₹{payout.TDS_deducted_amount}</TableCell>

                  <TableCell>₹{payout.convenience}</TableCell>

                  <TableCell>₹{payout.approne_charges}</TableCell>

                  <TableCell>₹{payout.police_verification_charges}</TableCell>

                  <TableCell sx={{ fontWeight: 700, color: "#2E7D32" }}>
                    ₹{payout.net_amount}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        </motion.div>
      </AnimatePresence>

      {/* ===================== ACTION BAR ===================== */}

      {/* ===================== DIALOGS ===================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViewApprovepayoutmodal;
